import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  ShipBobPerformanceTotals,
  ShipSpeedRow,
} from 'models/ShipBobPerformanceModel';

function formatMoney(value: number | null): string {
  return value == null ? '—' : `$${value.toFixed(2)}`;
}

function formatPct(value: number | null): string {
  return value == null ? '—' : `${value.toFixed(1)}%`;
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

interface CardProps {
  label: string;
  value: string;
  caption?: string;
}

function Card({ label, value, caption }: CardProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 180 }}>
      <Typography
        variant="overline"
        sx={{ color: '#64748B', lineHeight: 1.5, display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

interface SummaryCardsProps {
  totals: ShipBobPerformanceTotals;
  speedOverall?: ShipSpeedRow;
}

export default function SummaryCards({
  totals,
  speedOverall,
}: SummaryCardsProps) {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Card
        label="Avg Cost / Order"
        value={formatMoney(totals.costPerOrder)}
        caption={`total ${formatMoney(totals.totalCost)}`}
      />
      <Card
        label="On-Time Ship %"
        value={formatPct(totals.onTimePct)}
        caption={`${formatCount(totals.onTimeShipments)} of ${formatCount(
          totals.gradeableShipments,
        )} graded shipments (ShipBob's own SLA verdict)`}
      />
      {speedOverall && (
        <Card
          label="Shipped ≤2 Business Days"
          value={formatPct(speedOverall.within2Pct)}
          caption={`≤1 BD ${formatPct(speedOverall.within1Pct)} · avg ${
            speedOverall.avgBusinessDays == null
              ? '—'
              : speedOverall.avgBusinessDays.toFixed(2)
          } BD (our calc, order → ship)`}
        />
      )}
      {totals.avgTransitDays != null && (
        <Card
          label="Avg Transit Time"
          value={`${totals.avgTransitDays.toFixed(2)} d`}
          caption={`ship → delivered${
            totals.deliveredPct == null
              ? ''
              : ` · ${totals.deliveredPct.toFixed(1)}% delivered`
          }`}
        />
      )}
      <Card
        label="Split Shipment Rate"
        value={formatPct(totals.splitRatePct)}
        caption={`${formatCount(totals.splitOrders)} orders in >1 shipment`}
      />
      <Card label="Orders" value={formatCount(totals.orders)} />
      <Card
        label="Shipments"
        value={formatCount(totals.shipments)}
        caption={`cost/shipment ${formatMoney(totals.costPerShipment)}`}
      />
    </Box>
  );
}
